/**
 * RESIZE OPERATION SAFETY SYSTEM
 * 
 * This module provides safety checks and enhanced handling for resize operations
 * to prevent wall assignment confusion when dealing with shared walls and 
 * coordinate collisions. It integrates with the shared wall system to ensure
 * resize operations are performed safely.
 */

import { Wall, Point, Room } from '../types';
import { WallWithTempId, sharedWallSystem } from './sharedWallCoordinateSystem';
import { enhancedWallLookup, findWallSafeForResize } from './enhancedWallLookupSystem';
import { synchronizeSharedWallsAfterResize } from './floorPlanGeometry';

export interface ResizeOperationContext {
  roomId: string;
  roomName?: string;
  operationType: 'standard_resize' | 'partial_drag' | 'extrusion' | 'split_wall';
  maintainConnections?: boolean;
  allowSharedWallResize?: boolean;
  coordinateTolerance?: number;
}

export interface ResizeValidationResult {
  isValid: boolean;
  canProceed: boolean;
  blockers: string[];
  warnings: string[];
  suggestions: string[];
  affectedRooms: string[];
  sharedWallsCount: number;
}

export interface SafeResizeResult {
  resizedWalls: WallWithTempId[];
  allUpdatedWalls: WallWithTempId[];
  affectedRooms: string[];
  sharedWallGroups: string[];
  coordinateCollisionsResolved: number;
  safetyActionsApplied: string[];
  warnings: string[];
}

export interface ResizeConflictResolution {
  strategy: 'abort' | 'proceed_with_warning' | 'modify_target' | 'create_split';
  modifiedTarget?: Point;
  splitWallData?: { originalWall: WallWithTempId; splitPoint: Point };
  explanation: string;
}

/**
 * Main class for managing safe resize operations
 */
export class ResizeOperationSafetySystem {
  private readonly DEFAULT_TOLERANCE = 0.1;
  private readonly MAX_SHARED_WALLS_PER_OPERATION = 6;

  /**
   * Validate a resize operation before execution
   */
  public validateResizeOperation(
    wallToResize: WallWithTempId,
    newStart: Point,
    newEnd: Point,
    context: ResizeOperationContext,
    allWalls: WallWithTempId[],
    allRooms: Room[]
  ): ResizeValidationResult {
    const blockers: string[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];
    const affectedRooms = new Set<string>();
    let sharedWallsCount = 0;

    console.log('🛡️ VALIDATING RESIZE OPERATION:', {
      wallId: wallToResize.tempId,
      roomId: context.roomId,
      operationType: context.operationType,
      currentCoords: `(${wallToResize.start.x.toFixed(1)},${wallToResize.start.y.toFixed(1)})->(${wallToResize.end.x.toFixed(1)},${wallToResize.end.y.toFixed(1)})`,
      targetCoords: `(${newStart.x.toFixed(1)},${newStart.y.toFixed(1)})->(${newEnd.x.toFixed(1)},${newEnd.y.toFixed(1)})`
    });

    // Check 1: Wall ownership and authorization
    const safetyCheck = enhancedWallLookup.findWallForResize(wallToResize, context.roomId, allWalls);
    console.log('🛡️ CHECK 1 - Wall Authorization:', {
      isAuthorizedForResize: safetyCheck.isAuthorizedForResize,
      sharedWithRoomsCount: safetyCheck.sharedWithRooms.length,
      allowSharedWallResize: context.allowSharedWallResize
    });
    
    if (!safetyCheck.isAuthorizedForResize) {
      const blocker = `Room ${context.roomId} is not authorized to resize this wall`;
      console.log('🚫 BLOCKER 1A:', blocker);
      blockers.push(blocker);
    }

    if (safetyCheck.sharedWithRooms.length > 0) {
      sharedWallsCount++;
      affectedRooms.add(context.roomId);
      safetyCheck.sharedWithRooms.forEach(roomId => affectedRooms.add(roomId));
      
      if (!context.allowSharedWallResize) {
        const blocker = 'Shared wall resize not allowed in current context';
        console.log('🚫 BLOCKER 1B:', blocker);
        blockers.push(blocker);
      } else {
        warnings.push(`Resize will affect shared wall used by rooms: ${safetyCheck.sharedWithRooms.join(', ')}`);
      }
    }

    // Check 2: Coordinate collision at target position
    const targetCollisionCheck = enhancedWallLookup.findWallsAtCoordinates(
      { start: newStart, end: newEnd },
      allWalls,
      { 
        preferredRoomId: context.roomId,
        tolerance: context.coordinateTolerance || this.DEFAULT_TOLERANCE 
      }
    );

    console.log('🛡️ CHECK 2 - Coordinate Collision:', {
      hasMultipleMatches: targetCollisionCheck.hasMultipleMatches,
      matchCount: targetCollisionCheck.allMatches?.length || 0
    });

    if (targetCollisionCheck.hasMultipleMatches) {
      warnings.push(`Target position would create coordinate collision with ${targetCollisionCheck.allMatches.length} existing walls`);
      
      // Check if any of the colliding walls belong to different rooms
      const foreignWalls = targetCollisionCheck.allMatches.filter(wall => 
        !wall.assignedRoomIds?.includes(context.roomId) && 
        wall.originatingRoomId !== context.roomId
      );

      console.log('🛡️ CHECK 2A - Foreign Walls:', {
        foreignWallsCount: foreignWalls.length,
        allMatchesCount: targetCollisionCheck.allMatches.length
      });

      if (foreignWalls.length > 0) {
        const blocker = 'Target position would collide with walls from other rooms';
        console.log('🚫 BLOCKER 2:', blocker);
        blockers.push(blocker);
        suggestions.push('Adjust target position to avoid coordinate collision');
      }
    }

    // Check 3: Excessive shared wall count
    console.log('🛡️ CHECK 3 - Shared Wall Count:', {
      sharedWallsCount,
      maxAllowed: this.MAX_SHARED_WALLS_PER_OPERATION
    });
    
    if (sharedWallsCount > this.MAX_SHARED_WALLS_PER_OPERATION) {
      const blocker = `Too many shared walls in operation (${sharedWallsCount} > ${this.MAX_SHARED_WALLS_PER_OPERATION})`;
      console.log('🚫 BLOCKER 3:', blocker);
      blockers.push(blocker);
      suggestions.push('Consider breaking resize into smaller operations');
    }

    // Check 4: Room geometry validity after resize
    const geometryValidation = this.validatePostResizeGeometry(
      wallToResize,
      newStart,
      newEnd,
      context.roomId,
      allWalls,
      allRooms,
      context
    );

    console.log('🛡️ CHECK 4 - Geometry Validation:', {
      isValid: geometryValidation.isValid,
      issuesCount: geometryValidation.issues.length,
      issues: geometryValidation.issues
    });

    if (!geometryValidation.isValid) {
      console.log('🚫 BLOCKER 4:', geometryValidation.issues);
      blockers.push(...geometryValidation.issues);
      suggestions.push(...geometryValidation.suggestions);
    }

    const canProceed = blockers.length === 0;
    const isValid = canProceed && warnings.length < 3; // Arbitrary threshold

    console.log('🛡️ RESIZE VALIDATION COMPLETE:', {
      isValid,
      canProceed,
      blockerCount: blockers.length,
      warningCount: warnings.length,
      affectedRoomCount: affectedRooms.size,
      sharedWallsCount
    });

    return {
      isValid,
      canProceed,
      blockers,
      warnings,
      suggestions,
      affectedRooms: Array.from(affectedRooms),
      sharedWallsCount
    };
  }

  /**
   * Perform a safe resize operation with all safety checks
   */
  public performSafeResize(
    wallToResize: WallWithTempId,
    newStart: Point,
    newEnd: Point,
    context: ResizeOperationContext,
    allWalls: WallWithTempId[],
    allRooms: Room[]
  ): SafeResizeResult {
    const safetyActionsApplied: string[] = [];
    const warnings: string[] = [];
    const affectedRooms = new Set<string>([context.roomId]);
    const sharedWallGroups = new Set<string>();
    let coordinateCollisionsResolved = 0;

    console.log('🔄 PERFORMING SAFE RESIZE:', {
      wallId: wallToResize.tempId,
      roomId: context.roomId,
      operationType: context.operationType
    });

    // Step 1: Validate operation
    const validation = this.validateResizeOperation(
      wallToResize,
      newStart,
      newEnd,
      context,
      allWalls,
      allRooms
    );

    if (!validation.canProceed) {
      throw new Error(`Resize operation blocked: ${validation.blockers.join(', ')}`);
    }

    warnings.push(...validation.warnings);
    validation.affectedRooms.forEach(roomId => affectedRooms.add(roomId));

    // Step 2: Handle shared wall groups
    const sharedGroup = sharedWallSystem.getSharedWallGroup(wallToResize.tempId || '');
    if (sharedGroup) {
      sharedWallGroups.add(sharedGroup.groupId);
      sharedGroup.ownerRoomIds.forEach(roomId => affectedRooms.add(roomId));
      safetyActionsApplied.push('Identified shared wall group for coordinated update');
    }

    // Step 3: Perform the resize
    const resizedWall: WallWithTempId = {
      ...wallToResize,
      start: { ...newStart },
      end: { ...newEnd }
    };

    // Step 4: Check for new coordinate collisions created by resize
    const postResizeCollisionCheck = enhancedWallLookup.findWallsAtCoordinates(
      { start: newStart, end: newEnd },
      allWalls.filter(w => w.tempId !== wallToResize.tempId), // Exclude the wall being resized
      { preferredRoomId: context.roomId }
    );

    if (postResizeCollisionCheck.hasMultipleMatches) {
      coordinateCollisionsResolved++;
      safetyActionsApplied.push('Resolved coordinate collision at target position');
      
      // Merge ownership if needed
      const collidingWalls = postResizeCollisionCheck.allMatches;
      for (const collidingWall of collidingWalls) {
        if (collidingWall.assignedRoomIds && !collidingWall.assignedRoomIds.includes(context.roomId)) {
          sharedWallSystem.mergeWallOwnership(collidingWall, context.roomId);
          safetyActionsApplied.push(`Merged wall ownership for wall ${collidingWall.tempId}`);
        }
      }
    }

    // Step 5: Synchronize all affected walls
    const resizedWalls = [resizedWall];
    const synchronizedWalls = context.maintainConnections !== false 
      ? synchronizeSharedWallsAfterResize(resizedWalls, allWalls, context.coordinateTolerance || this.DEFAULT_TOLERANCE)
      : allWalls.map(w => w.tempId === wallToResize.tempId ? resizedWall : w);

    if (synchronizedWalls.length !== allWalls.length) {
      safetyActionsApplied.push('Synchronized shared walls after resize');
    }

    console.log('✅ SAFE RESIZE COMPLETE:', {
      wallId: wallToResize.tempId,
      roomId: context.roomId,
      affectedRoomCount: affectedRooms.size,
      sharedWallGroupCount: sharedWallGroups.size,
      safetyActionsCount: safetyActionsApplied.length,
      coordinateCollisionsResolved
    });

    return {
      resizedWalls,
      allUpdatedWalls: synchronizedWalls,
      affectedRooms: Array.from(affectedRooms),
      sharedWallGroups: Array.from(sharedWallGroups),
      coordinateCollisionsResolved,
      safetyActionsApplied,
      warnings
    };
  }

  /**
   * Resolve conflicts when resize operation encounters problems
   */
  public resolveResizeConflict(
    wallToResize: WallWithTempId,
    targetStart: Point,
    targetEnd: Point,
    context: ResizeOperationContext,
    conflictReason: string
  ): ResizeConflictResolution {
    console.log('🚨 RESOLVING RESIZE CONFLICT:', {
      wallId: wallToResize.tempId,
      roomId: context.roomId,
      conflictReason
    });

    // Strategy 1: Try slight offset to avoid collision
    if (conflictReason.includes('coordinate collision')) {
      const offsetTarget = this.calculateSafeOffset(targetStart, targetEnd, 2.0);
      if (offsetTarget) {
        return {
          strategy: 'modify_target',
          modifiedTarget: offsetTarget.start,
          explanation: 'Applied small offset to avoid coordinate collision'
        };
      }
    }

    // Strategy 2: Create wall split if geometry allows
    if (conflictReason.includes('foreign wall') && context.operationType === 'standard_resize') {
      const splitPoint = this.calculateOptimalSplitPoint(wallToResize, targetStart, targetEnd);
      if (splitPoint) {
        return {
          strategy: 'create_split',
          splitWallData: { originalWall: wallToResize, splitPoint },
          explanation: 'Created wall split to maintain room boundaries'
        };
      }
    }

    // Strategy 3: Proceed with warning for minor issues
    if (!conflictReason.includes('blocked') && !conflictReason.includes('unauthorized')) {
      return {
        strategy: 'proceed_with_warning',
        explanation: `Proceeding with resize despite: ${conflictReason}`
      };
    }

    // Strategy 4: Abort for serious conflicts
    return {
      strategy: 'abort',
      explanation: `Cannot safely proceed due to: ${conflictReason}`
    };
  }

  /**
   * Validate room geometry after resize would be applied
   */
  private validatePostResizeGeometry(
    wallToResize: WallWithTempId,
    newStart: Point,
    newEnd: Point,
    roomId: string,
    allWalls: WallWithTempId[],
    allRooms: Room[],
    context: ResizeOperationContext
  ): { isValid: boolean; issues: string[]; suggestions: string[] } {
    const issues: string[] = [];
    const suggestions: string[] = [];

    console.log('🔍 GEOMETRY VALIDATION START:', {
      wallId: wallToResize.tempId,
      roomId,
      newCoords: `(${newStart.x.toFixed(1)},${newStart.y.toFixed(1)})->(${newEnd.x.toFixed(1)},${newEnd.y.toFixed(1)})`,
      allRoomsCount: allRooms.length
    });

    // Find the room that contains this wall
    const room = allRooms.find(r => r.stableId === roomId || r.id === roomId);
    console.log('🔍 ROOM LOOKUP:', {
      roomFound: !!room,
      roomId,
      searchedBy: 'stableId and id'
    });
    
    if (!room) {
      const issue = `Room ${roomId} not found`;
      console.log('🚫 GEOMETRY ISSUE 1:', issue);
      issues.push(issue);
      return { isValid: false, issues, suggestions };
    }

    // Check if resize would create invalid geometry
    const wallLength = Math.hypot(newEnd.x - newStart.x, newEnd.y - newStart.y);
    console.log('🔍 WALL LENGTH CHECK:', {
      wallLength: wallLength.toFixed(2),
      minRequired: 0.1,
      isValid: wallLength >= 0.1
    });
    
    if (wallLength < 0.1) {
      const issue = 'Resize would create wall with zero or negative length';
      console.log('🚫 GEOMETRY ISSUE 2:', issue);
      issues.push(issue);
      suggestions.push('Ensure target coordinates create a valid wall segment');
    }

    // Check for self-intersection (skip for room resize operations as connected walls will adjust)
    const isRoomResizeOperation = context?.operationType === 'standard_resize';
    const otherRoomWalls = room.walls.filter(w => w !== wallToResize);
    const resizedWall = { start: newStart, end: newEnd };
    
    console.log('🔍 SELF-INTERSECTION CHECK:', {
      otherWallsCount: otherRoomWalls.length,
      resizedWallCoords: `(${newStart.x.toFixed(1)},${newStart.y.toFixed(1)})->(${newEnd.x.toFixed(1)},${newEnd.y.toFixed(1)})`,
      isRoomResizeOperation,
      skipIntersectionCheck: isRoomResizeOperation
    });
    
    if (isRoomResizeOperation) {
      console.log('⚠️ SKIPPING INTERSECTION CHECK: Room resize operations handle connected wall adjustments automatically');
    } else {
      for (let i = 0; i < otherRoomWalls.length; i++) {
        const otherWall = otherRoomWalls[i];
        const intersects = this.doWallsIntersect(resizedWall, otherWall);
        
        console.log(`🔍 INTERSECTION CHECK ${i}:`, {
          otherWallCoords: `(${otherWall.start.x.toFixed(1)},${otherWall.start.y.toFixed(1)})->(${otherWall.end.x.toFixed(1)},${otherWall.end.y.toFixed(1)})`,
          intersects
        });
        
        if (intersects) {
          const issue = 'Resize would create self-intersecting room geometry';
          console.log('🚫 GEOMETRY ISSUE 3:', issue);
          issues.push(issue);
          suggestions.push('Adjust target position to avoid wall intersections');
          break;
        }
      }
    }

    console.log('🔍 GEOMETRY VALIDATION RESULT:', {
      isValid: issues.length === 0,
      issuesCount: issues.length,
      issues
    });

    return {
      isValid: issues.length === 0,
      issues,
      suggestions
    };
  }

  /**
   * Calculate a safe offset to avoid coordinate collisions
   */
  private calculateSafeOffset(start: Point, end: Point, offsetDistance: number): { start: Point; end: Point } | null {
    // Calculate perpendicular offset direction
    const wallVector = { x: end.x - start.x, y: end.y - start.y };
    const wallLength = Math.hypot(wallVector.x, wallVector.y);
    
    if (wallLength === 0) return null;
    
    const perpendicular = { 
      x: -wallVector.y / wallLength * offsetDistance, 
      y: wallVector.x / wallLength * offsetDistance 
    };

    return {
      start: { x: start.x + perpendicular.x, y: start.y + perpendicular.y },
      end: { x: end.x + perpendicular.x, y: end.y + perpendicular.y }
    };
  }

  /**
   * Calculate optimal split point for wall splitting
   */
  private calculateOptimalSplitPoint(wall: WallWithTempId, targetStart: Point, targetEnd: Point): Point | null {
    // Simple midpoint split for now - could be enhanced with more sophisticated logic
    return {
      x: (wall.start.x + wall.end.x) / 2,
      y: (wall.start.y + wall.end.y) / 2
    };
  }

  /**
   * Check if two walls intersect
   */
  private doWallsIntersect(wall1: { start: Point; end: Point }, wall2: { start: Point; end: Point }): boolean {
    // Simple line intersection check
    const det = (wall1.end.x - wall1.start.x) * (wall2.end.y - wall2.start.y) - 
                (wall2.end.x - wall2.start.x) * (wall1.end.y - wall1.start.y);
    
    if (Math.abs(det) < 1e-10) return false; // Parallel lines
    
    const t = ((wall2.start.x - wall1.start.x) * (wall2.end.y - wall2.start.y) - 
               (wall2.start.y - wall1.start.y) * (wall2.end.x - wall2.start.x)) / det;
               
    const u = -((wall1.start.x - wall1.end.x) * (wall1.start.y - wall2.start.y) - 
                (wall1.start.y - wall1.end.y) * (wall1.start.x - wall2.start.x)) / det;
    
    return t >= 0 && t <= 1 && u >= 0 && u <= 1;
  }

  /**
   * Get diagnostic information about resize safety system
   */
  public getDiagnostics(): {
    activeOperations: number;
    conflictsResolved: number;
    safetyChecksPerformed: number;
  } {
    // In a real implementation, these would be tracked
    return {
      activeOperations: 0,
      conflictsResolved: 0,
      safetyChecksPerformed: 0
    };
  }
}

// Global instance
export const resizeSafetySystem = new ResizeOperationSafetySystem();

/**
 * Convenience function for safe wall resize with validation
 */
export function performSafeWallResize(
  wallToResize: WallWithTempId,
  newStart: Point,
  newEnd: Point,
  roomId: string,
  allWalls: WallWithTempId[],
  allRooms: Room[],
  options: Partial<ResizeOperationContext> = {}
): SafeResizeResult {
  const context: ResizeOperationContext = {
    roomId,
    operationType: 'standard_resize',
    maintainConnections: true,
    allowSharedWallResize: true,
    coordinateTolerance: 0.1,
    ...options
  };

  return resizeSafetySystem.performSafeResize(
    wallToResize,
    newStart,
    newEnd,
    context,
    allWalls,
    allRooms
  );
}

/**
 * Convenience function for resize validation only
 */
export function validateWallResize(
  wallToResize: WallWithTempId,
  newStart: Point,
  newEnd: Point,
  roomId: string,
  allWalls: WallWithTempId[],
  allRooms: Room[]
): ResizeValidationResult {
  const context: ResizeOperationContext = {
    roomId,
    operationType: 'standard_resize',
    allowSharedWallResize: true
  };

  return resizeSafetySystem.validateResizeOperation(
    wallToResize,
    newStart,
    newEnd,
    context,
    allWalls,
    allRooms
  );
}